import { useState } from 'react';
import { Ban, Mail, RotateCcw, Send, ShieldCheck, UserCog, UserPlus } from 'lucide-react';
import { SignInAsAction, useSignInAs } from '../../admin/components/ImpersonateDialog';
import {
  Alert, Button, ConfirmDialog, DataTable, IconAction, Input, RowActions, SearchInput, StatusBadge, type Tone, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { formatDate, formatDateTime } from '../../../lib/format';
import { signoffGap, signoffGapText } from '../../../lib/labSignoff';
import { VenueSections } from '../components/VenueSections';

interface StaffRow {
  id: string;
  email: string;
  fullName: string;
  status: string;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
  suspendedReason: string | null;
  roles: Array<{ key: string; name: string }>;
}

interface GrantableRole { key: string; name: string; description: string }

const TONE: Record<string, Tone> = {
  ACTIVE: 'success', INVITED: 'warning', SUSPENDED: 'danger', DEACTIVATED: 'neutral',
};
const STATUS_WORD: Record<string, string> = {
  ACTIVE: 'Active', INVITED: 'Invited', SUSPENDED: 'Suspended', DEACTIVATED: 'Deactivated', LOCKED: 'Locked',
};

/**
 * What a venue role is called on screen. The server's names are mostly fine,
 * but the administrator is called "Lab Admin" even at a hospital, and a role
 * with no name would otherwise print its key.
 */
const VENUE_ROLE_LABEL: Record<string, string> = {
  PARTNER_ADMIN: 'Venue admin',
  HOSPITAL_COORDINATOR: 'Front desk coordinator',
  HOSPITAL_TECHNICIAN: 'Station technician',
  LAB_TECHNICIAN: 'Lab technician',
  PATHOLOGIST: 'Pathologist',
};
export function venueRoleLabel(r: { key: string; name?: string | null }): string {
  if (VENUE_ROLE_LABEL[r.key]) return VENUE_ROLE_LABEL[r.key];
  const name = r.name?.trim();
  if (name && !/^[A-Z0-9_]+$/.test(name)) return name;
  const words = (name || r.key).toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The people who work at this venue.
 *
 * One list, because the venue is one organisation however many companies book
 * into it. Nobody here belongs to a company — a hospital's technician is the
 * hospital's, and the workers they see belong to whoever sent them.
 *
 * An administrator hires technicians. Appointing another administrator is the
 * platform's act, so the role is not offered; the server refuses it too, and
 * the two agreeing is not an accident — the list of roles this screen renders
 * is the server's answer to "what could you actually grant", not a constant
 * here that could drift from it.
 */
export function Staff() {
  const { principal } = useAuth();
  const venue = principal?.partner;

  if (!venue) {
    return (
      <Alert tone="danger">
        This screen belongs to a hospital or laboratory account. Yours is not one.
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Users"
        hint={`Everyone who signs in for ${venue.name}. They work for the venue, for every company that books into it.`}
      />
      <VenueSections />
      <VenueStaff venueId={venue.id} />
    </div>
  );
}

/**
 * A venue's people, with what can be done to each.
 *
 * Shared by the venue's own Staff screen and the platform's venue page, so
 * both read the same list and act through the same endpoints. What a reader
 * may do is decided by the server; the buttons follow the permissions the
 * reader holds. A venue admin may move people between the roles it can grant;
 * appointing or demoting an administrator stays with the platform.
 */
export function VenueStaff({ venueId, platform = false }: { venueId: string; platform?: boolean }) {
  const { principal, permissions } = useAuth();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [suspending, setSuspending] = useState<StaffRow | null>(null);
  const [reinstating, setReinstating] = useState<StaffRow | null>(null);
  const [changingRole, setChangingRole] = useState<StaffRow | null>(null);
  const canCreate = holds(permissions, 'user.create');
  const canSuspend = holds(permissions, 'user.suspend');
  const canChangeRole = holds(permissions, 'user.role-assign');
  // The platform's Super Admin signs straight in as a venue's staff (owner, 29 Oct 2026).
  const canSignIn = platform && holds(permissions, 'platform.impersonate');
  const signIn = useSignInAs();

  const staff = useQuery<{ items: StaffRow[] }>(
    () => api.get(`/partners/${venueId}/users`), [venueId],
  );

  const [status, setStatus] = useState<'' | 'ACTIVE' | 'INVITED' | 'SUSPENDED'>('');
  const term = q.trim().toLowerCase();
  const all = staff.data?.items ?? [];
  const rows = all.filter(
    (u) => (!status || u.status === status) && (!term
      || u.fullName.toLowerCase().includes(term)
      || u.email.toLowerCase().includes(term)
      || u.roles.some((r) => venueRoleLabel(r).toLowerCase().includes(term))),
  );
  // Who does what, among the people who can sign in today: the shape of the
  // team in one line, so "no pathologist" is seen before a result is stuck.
  const activeByRole = new Map<string, number>();
  for (const u of all) if (u.status === 'ACTIVE') for (const r of u.roles) {
    const label = venueRoleLabel(r);
    activeByRole.set(label, (activeByRole.get(label) ?? 0) + 1);
  }
  const countOf = (st: string) => all.filter((u) => u.status === st).length;
  const invited = (staff.data?.items ?? []).filter((u) => u.status === 'INVITED');
  // Technicians enter results; only a Pathologist, and never the person who
  // entered a value, validates and releases it. Said here, where the fix is,
  // before a case is found stuck (flow audit 10.2). Not said to a venue with
  // no technicians — it does no laboratory work to sign.
  const gap = staff.data ? signoffGap(staff.data.items) : null;
  const gapText = gap && (gap.kind === 'SIGNER_ENTERS' || (gap.kind === 'NO_SIGNER' && gap.enterers > 0))
    ? signoffGapText(gap) : null;

  return (
    <div className="space-y-4">
      {gapText && (
        <Alert tone="warning" title={gapText.title}>
          {gapText.detail}
          {canCreate && (
            <>
              {' '}
              <button type="button" onClick={() => setAdding(true)} className="font-semibold underline underline-offset-2">
                Add user
              </button>
            </>
          )}
        </Alert>
      )}

      {invited.length > 0 && (
        <Alert tone="info" title={`${invited.length} invitation${invited.length === 1 ? '' : 's'} not accepted yet`}>
          An invitation link works for 72 hours. If it has run out, send a new one from the person&rsquo;s row.
        </Alert>
      )}

      {activeByRole.size > 0 && (
        <p className="text-small text-neutral-700">
          <span className="font-medium text-neutral-900">Active team: </span>
          {[...activeByRole].map(([label, n]) => `${n} ${label.toLowerCase()}${n === 1 ? '' : 's'}`).join(' · ')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {all.length > 0 && (
          <div role="group" aria-label="Show users by status" className="flex flex-wrap gap-1.5">
            {([['', 'All', all.length], ['ACTIVE', 'Active', countOf('ACTIVE')], ['INVITED', 'Invited', countOf('INVITED')], ['SUSPENDED', 'Suspended', countOf('SUSPENDED')]] as const)
              .filter(([k, , n]) => !k || n > 0)
              .map(([k, label, n]) => (
                <button key={k || 'all'} type="button" aria-pressed={status === k} onClick={() => setStatus(k)}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-small font-medium transition-colors max-md:h-10 ${status === k ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50'}`}>
                  {label}<span className="tabular-nums text-neutral-500">{n}</span>
                </button>
              ))}
          </div>
        )}
        <SearchInput
          value={q} onValueChange={setQ}
          placeholder="Name, email or role" aria-label="Search users" className="min-w-0 flex-[1_1_14rem] sm:max-w-xs"
        />
        {canCreate && (
          <Button className="ml-auto" onClick={() => setAdding(true)} icon={<UserPlus className="h-4 w-4" aria-hidden />}>
            Add user
          </Button>
        )}
      </div>

      {signIn.error && <Alert tone="danger">{signIn.error}</Alert>}
      <DataTable<StaffRow>
        rows={rows}
        loading={staff.loading}
        error={staff.error}
        onRetry={staff.reload}
        rowKey={(u) => u.id}
        empty={term || status ? {
          title: 'Nobody matches',
          hint: 'Try part of a name or an email address, or show all statuses.',
          action: <Button variant="secondary" onClick={() => { setQ(''); setStatus(''); }}>Clear filters</Button>,
        } : {
          title: 'No users yet',
          hint: 'Add a front desk coordinator to run the queue and check-in, lab technicians to draw samples and enter results, and a pathologist to release them.',
          action: canCreate ? <Button onClick={() => setAdding(true)}>Add user</Button> : undefined,
        }}
        columns={[
          {
            key: 'person', header: 'Person', alwaysVisible: true,
            cell: (u) => (
              <div className="min-w-0">
                <div className="truncate text-small font-medium text-neutral-900">
                  {u.fullName}{u.id === principal?.id ? ' (you)' : ''}
                </div>
                <div className="truncate text-caption text-neutral-500">{u.email}</div>
              </div>
            ),
          },
          {
            key: 'role', header: 'Role',
            cell: (u) => (
              <span className="inline-flex items-center gap-1.5 text-small text-neutral-800">
                {u.roles.some((r) => r.key === 'PARTNER_ADMIN') && <ShieldCheck className="h-4 w-4 text-primary-600" aria-hidden />}
                {u.roles.map(venueRoleLabel).join(', ') || 'No role'}
              </span>
            ),
          },
          {
            key: 'status', header: 'Status',
            cell: (u) => (
              <div>
                <StatusBadge tone={TONE[u.status] ?? 'neutral'}>
                  {STATUS_WORD[u.status] ?? venueRoleLabel({ key: u.status })}
                </StatusBadge>
                {u.status === 'SUSPENDED' && u.suspendedReason && (
                  <div className="mt-1 max-w-xs text-caption text-neutral-600">{u.suspendedReason}</div>
                )}
              </div>
            ),
          },
          {
            key: 'last', header: 'Last signed in',
            cell: (u) => (
              <span className="text-small tabular-nums text-neutral-700">
                {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}
              </span>
            ),
          },
          {
            key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (u) => {
              const self = u.id === principal?.id;
              const isTargetAdmin = u.roles.some((r) => r.key === 'PARTNER_ADMIN');
              const mayTouch = !self && (platform || !isTargetAdmin);
              const invite = u.status === 'INVITED' && canCreate;
              const role = canChangeRole && mayTouch && u.status !== 'SUSPENDED';
              const suspend = canSuspend && mayTouch && u.status !== 'SUSPENDED' && u.status !== 'INVITED';
              const reinstate = canSuspend && mayTouch && u.status === 'SUSPENDED';
              const signInAs = canSignIn && !self && u.status === 'ACTIVE';
              if (!invite && !role && !suspend && !reinstate && !signInAs) return null;
              return (
                <RowActions>
                  {signInAs && (
                    <SignInAsAction name={u.fullName} path={`/admin/users/${u.id}/impersonate`} signIn={signIn} />
                  )}
                  {invite && (
                    <SendInvitation
                      venueId={venueId}
                      user={u}
                      onSent={(email, expiresAt) => toast(
                        'success',
                        `Invitation sent to ${email}. The link works until ${formatDateTime(expiresAt)}.`,
                      )}
                    />
                  )}
                  {role && (
                    <IconAction label={`Change role of ${u.fullName}`} icon={UserCog} onClick={() => setChangingRole(u)} />
                  )}
                  {reinstate && (
                    <IconAction label={`Reinstate ${u.fullName}`} icon={RotateCcw} onClick={() => setReinstating(u)} />
                  )}
                  {/* Kept apart from the everyday actions and drawn as the
                      one that ends someone's access. */}
                  {suspend && (
                    <span className="flex items-center border-l border-neutral-200 pl-1">
                      <IconAction label={`Suspend ${u.fullName}`} icon={Ban} danger onClick={() => setSuspending(u)} />
                    </span>
                  )}
                </RowActions>
              );
            },
          },
        ]}
      />

      {adding && (
        <AddDialog
          venueId={venueId}
          onClose={() => setAdding(false)}
          onDone={(sent) => {
            setAdding(false);
            staff.reload();
            toast('success', sent
              ? 'Account created and the invitation is on its way.'
              : 'Account created, but the invitation email did not go. Send it from their row.');
          }}
        />
      )}

      {suspending && (
        <ConfirmDialog
          title={`Suspend ${suspending.fullName}?`}
          what="They can no longer sign in, and every session they have open ends now."
          affects={`${suspending.fullName} · ${suspending.roles.map(venueRoleLabel).join(', ') || 'no role'} · ${suspending.email}. Results they entered and readings they took stay on the record under their name.`}
          irreversible="You can reinstate them later from this list."
          confirmLabel="Suspend" tone="danger" requireReason minReason={5}
          reasonLabel="Why? (at least five characters, kept on the record)"
          onCancel={() => setSuspending(null)}
          onConfirm={async (reason) => {
            try {
              await api.post(`/partners/${venueId}/users/${suspending.id}/suspend`, { reason });
            } catch (e) { throw new Error(errorMessage(e)); }
            toast('success', `${suspending.fullName} is suspended.`);
            setSuspending(null); staff.reload();
          }}
        />
      )}

      {reinstating && (
        <ConfirmDialog
          title={`Reinstate ${reinstating.fullName}?`}
          what="They can sign in again with their existing password."
          affects={`Their role is unchanged: ${reinstating.roles.map(venueRoleLabel).join(', ') || 'no role'}.`}
          confirmLabel="Reinstate" requireReason minReason={5}
          reasonLabel="Why? (at least five characters, kept on the record)"
          onCancel={() => setReinstating(null)}
          onConfirm={async (reason) => {
            try {
              await api.post(`/partners/${venueId}/users/${reinstating.id}/reinstate`, { reason });
            } catch (e) { throw new Error(errorMessage(e)); }
            toast('success', `${reinstating.fullName} can sign in again.`);
            setReinstating(null); staff.reload();
          }}
        />
      )}

      {changingRole && (
        <ChangeVenueRoleDialog
          venueId={venueId}
          user={changingRole}
          onClose={() => setChangingRole(null)}
          onDone={(name) => {
            toast('success', `${changingRole.fullName} is now ${name}. Their open sessions were ended.`);
            setChangingRole(null); staff.reload();
          }}
        />
      )}
    </div>
  );
}

/** Platform only: moving a person between the venue's two roles. */
function ChangeVenueRoleDialog({ venueId, user, onClose, onDone }: {
  venueId: string; user: StaffRow; onClose: () => void; onDone: (roleName: string) => void;
}) {
  const roles = useQuery<{ items: GrantableRole[] }>(
    () => api.get(`/partners/${venueId}/users/roles`), [venueId],
  );
  const current = user.roles[0]?.key ?? '';
  const [roleKey, setRoleKey] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = roles.data?.items.find((r) => r.key === roleKey);

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.put(`/partners/${venueId}/users/${user.id}/role`, { roleKey });
      onDone(chosen ? venueRoleLabel(chosen) : venueRoleLabel({ key: roleKey }));
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Change ${user.fullName}'s role`} onClose={onClose}>
      <p className="mt-1 text-sm text-neutral-600">
        {user.fullName} is {user.roles.map(venueRoleLabel).join(', ') || 'without a role'} today. The new role replaces
        the old one and their open sessions end, so it applies at their next sign-in.
      </p>
      <fieldset className="mt-4 space-y-2">
        <legend className="sr-only">Role</legend>
        {(roles.data?.items ?? []).map((r) => (
          <label key={r.key} className="flex cursor-pointer items-center gap-2.5 rounded-input border border-neutral-200 p-3 hover:border-neutral-300">
            <input type="radio" name="venue-role" value={r.key} checked={roleKey === r.key} onChange={() => setRoleKey(r.key)} />
            <span className="text-sm font-medium text-neutral-900">{venueRoleLabel(r)}{r.key === current ? ' (current)' : ''}</span>
          </label>
        ))}
      </fieldset>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={!roleKey || roleKey === current}>Change role</Button>
      </div>
    </Dialog>
  );
}

/** Sends the emailed link, or sends it again — a resend kills the previous one. */
function SendInvitation({ venueId, user, onSent }: {
  venueId: string;
  user: StaffRow;
  onSent: (email: string, expiresAt: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ email: string; expiresAt: string }>(
        `/partners/${venueId}/users/${user.id}/invite`,
      );
      onSent(r.email, r.expiresAt);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <IconAction
        label={busy ? `Sending invitation to ${user.fullName}` : `Send invitation to ${user.fullName}`}
        icon={Send} primary loading={busy} onClick={go}
      />
      {error && <span role="alert" className="text-caption text-danger-600">{error}</span>}
    </div>
  );
}

function AddDialog({ venueId, onClose, onDone }: {
  venueId: string;
  onClose: () => void;
  onDone: (sent: boolean) => void;
}) {
  const roles = useQuery<{ items: GrantableRole[] }>(
    () => api.get(`/partners/${venueId}/users/roles`), [venueId],
  );
  const offered = roles.data?.items ?? [];
  const [form, setForm] = useState({ email: '', fullName: '', roleKey: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = offered.find((r) => r.key === form.roleKey);
  const ready = form.email.includes('@') && form.fullName.trim().length >= 2 && !!chosen;

  async function submit() {
    setBusy(true); setError(null);
    try {
      const created = await api.post<{ id: string }>(`/partners/${venueId}/users`, {
        email: form.email.trim().toLowerCase(),
        fullName: form.fullName,
        roleKey: form.roleKey,
      }, crypto.randomUUID());
      // The account exists either way; the email is the part that can fail —
      // no mail server, a suppressed address. Say so rather than undoing the
      // account, because the row offers a second try.
      let sent = true;
      try { await api.post(`/partners/${venueId}/users/${created.id}/invite`); } catch { sent = false; }
      onDone(sent);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Add someone to this venue" info="They get an account with no password and an email asking them to choose one. Nobody here ever sees it." onClose={onClose}>

      <label className="mt-4 block">
        <span className="text-caption text-neutral-700">Full name</span>
        <Input
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          className="mt-1 w-full" autoFocus
        />
      </label>

      <label className="mt-3 block">
        <span className="text-caption text-neutral-700">Work email</span>
        <span className="relative mt-1 block">
          <Mail
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
            aria-hidden
          />
          <Input
            type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full pl-9"
          />
        </span>
      </label>

      <fieldset className="mt-4">
        <legend className="text-caption text-neutral-700">Role</legend>
        <div className="mt-1.5 space-y-2">
          {offered.map((r) => (
            <label
              key={r.key}
              className="flex cursor-pointer items-center gap-2.5 rounded-input border border-neutral-200 p-3 hover:border-neutral-300"
            >
              <input
                type="radio" name="role" value={r.key}
                checked={form.roleKey === r.key}
                onChange={() => setForm({ ...form, roleKey: r.key })}
              />
              <span className="text-sm font-medium text-neutral-900">{venueRoleLabel(r)}</span>
            </label>
          ))}
          {!roles.loading && offered.length === 0 && (
            <p className="text-xs text-neutral-500">
              There is no role you can grant here.
            </p>
          )}
        </div>
      </fieldset>

      {error && <Alert className="mt-3">{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!ready}>Create and invite</Button>
      </div>
    </Dialog>
  );
}
